import React, { useState, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import { Collapsible, CollapsibleContent } from './ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from './ui/popover';
import {
  Columns3,
  Settings,
  Search,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Star,
  HelpCircle
} from 'lucide-react';

// Field categories for organization with enhanced metadata
const FIELD_CATEGORIES = {
  basic: {
    label: '基本信息',
    icon: '📊',
    description: '产品核心信息',
    fields: ['name', 'product_id', 'part_number', 'client_ref_number', 'category', 'specification', 'status', 'remark']
  },
  manufacturing: {
    label: '制造参数',
    icon: '⚙️',
    description: '生产工艺参数',
    fields: ['material', 'coating', 'diameter', 'length', 'hardness', 'tolerance', 'blade_type', 'guide_pillar_length', 'guide_pillar_tolerance', 'marking']
  },
  pricing: {
    label: '价格库存',
    icon: '💰',
    description: '价格与库存信息',
    fields: ['price', 'unit', 'min_order_qty', 'lead_time', 'stock_status', 'quantity', 'unit_price', 'total_price']
  },
  quality: {
    label: '质量合规',
    icon: '🏆',
    description: '质量认证标准',
    fields: ['certification', 'quality_grade', 'inspection_standard']
  }
};

const FIELD_METADATA = {
  // Basic Information
  name: { label: '产品名称', usage: 'high', description: '产品的主要名称标识' },
  product_id: { label: '产品ID', usage: 'high', description: '唯一产品标识符' },
  part_number: { label: '零件号', usage: 'medium', description: '制造商零件编号' },
  client_ref_number: { label: '物料编码', usage: 'medium', description: '客户内部物料编码' },
  category: { label: '类别', usage: 'medium', description: '产品分类' },
  specification: { label: '规格', usage: 'high', description: '产品详细规格说明' },
  status: { label: '状态', usage: 'medium', description: '产品当前状态' },
  remark: { label: '备注', usage: 'low', description: '附加说明信息' },

  // Manufacturing Parameters
  material: { label: '材料', usage: 'medium', description: '制造材料类型' },
  coating: { label: '涂层', usage: 'low', description: '表面涂层处理' },
  diameter: { label: '直径', usage: 'medium', description: '产品直径尺寸' },
  length: { label: '长度', usage: 'medium', description: '产品长度尺寸' },
  hardness: { label: '硬度', usage: 'low', description: '材料硬度指标' },
  tolerance: { label: '公差', usage: 'low', description: '制造公差范围' },
  blade_type: { label: '刃型', usage: 'low', description: '刀具刃型类型' },
  guide_pillar_length: { label: '导柱长度', usage: 'low', description: '导向柱长度' },
  guide_pillar_tolerance: { label: '导柱直径公差', usage: 'low', description: '导柱直径公差' },
  marking: { label: '打标', usage: 'low', description: '产品标记信息' },

  // Pricing & Inventory
  price: { label: '价格', usage: 'high', description: '产品单价' },
  unit: { label: '单位', usage: 'high', description: '计量单位' },
  min_order_qty: { label: '最小订货量', usage: 'medium', description: '最少采购数量' },
  lead_time: { label: '交期', usage: 'medium', description: '交付时间' },
  stock_status: { label: '库存状态', usage: 'medium', description: '当前库存状况' },
  quantity: { label: '数量', usage: 'high', description: '产品数量' },
  unit_price: { label: '单价(未税)', usage: 'high', description: '单位价格(不含税)' },
  total_price: { label: '总价(未税)', usage: 'high', description: '总计金额(不含税)', calculated: true },
  unit_price_tax_included: { label: '单价(含税)', usage: 'high', description: '单位价格(含税)', calculated: true },
  total_price_tax_included: { label: '总价(含税)', usage: 'high', description: '总计金额(含税)', calculated: true },

  // Quality & Compliance
  certification: { label: '认证', usage: 'low', description: '产品质量认证' },
  quality_grade: { label: '质量等级', usage: 'low', description: '质量等级标准' },
  inspection_standard: { label: '检验标准', usage: 'low', description: '检验执行标准' }
};

// Legacy compatibility
const FIELD_LABELS = Object.keys(FIELD_METADATA).reduce((acc, key) => {
  acc[key] = FIELD_METADATA[key].label;
  return acc;
}, {});

const ColumnSelector = ({
  currentColumns = [],
  availableFields = Object.keys(FIELD_LABELS),
  onColumnToggle,
  onOpenFullManager,
  onReset,
  requiredFields = ['name', 'product_id'],
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleToggleColumn = (fieldId, event) => {
    event?.stopPropagation();
    if (onColumnToggle) {
      onColumnToggle(fieldId);
    }
  };

  const handleOpenFullManager = () => {
    handleClose();
    if (onOpenFullManager) {
      onOpenFullManager();
    }
  };

  const handleReset = () => {
    handleClose();
    if (onReset) {
      onReset();
    }
  };

  const toggleCategory = useCallback((categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  }, []);

  const handleCategoryToggle = useCallback((categoryKey, show) => {
    const category = FIELD_CATEGORIES[categoryKey];
    if (!category || !onColumnToggle) return;

    category.fields.forEach(fieldId => {
      if (!availableFields.includes(fieldId) || requiredFields.includes(fieldId)) return;

      const isCurrentlyVisible = currentColumns.includes(fieldId);
      if (show && !isCurrentlyVisible) {
        onColumnToggle(fieldId);
      } else if (!show && isCurrentlyVisible) {
        onColumnToggle(fieldId);
      }
    });
  }, [availableFields, currentColumns, onColumnToggle, requiredFields]);

  // Organize and filter fields by category
  const categorizedFields = useMemo(() => {
    const result = Object.entries(FIELD_CATEGORIES).reduce((acc, [categoryKey, category]) => {
      let categoryFields = category.fields.filter(field => availableFields.includes(field));

      // Apply search filter
      if (searchTerm) {
        categoryFields = categoryFields.filter(field => {
          const metadata = FIELD_METADATA[field];
          const label = metadata?.label || field;
          const description = metadata?.description || '';
          return label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 field.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }

      if (categoryFields.length > 0) {
        acc[categoryKey] = {
          ...category,
          fields: categoryFields
        };
      }
      return acc;
    }, {});

    // Auto-expand categories when searching or initially expand basic
    if (searchTerm) {
      Object.keys(result).forEach(key => {
        setExpandedCategories(prev => ({ ...prev, [key]: true }));
      });
    } else if (Object.keys(expandedCategories).length === 0) {
      setExpandedCategories({ basic: true, pricing: true });
    }

    return result;
  }, [availableFields, searchTerm, expandedCategories]);

  // Count visible columns
  const visibleCount = currentColumns.length;
  const totalCount = availableFields.length;

  // Get usage indicator
  const getUsageIndicator = (usage) => {
    switch (usage) {
      case 'high': return { stars: 3, color: 'text-success' };
      case 'medium': return { stars: 2, color: 'text-warning' };
      case 'low': return { stars: 1, color: 'text-muted-foreground' };
      default: return { stars: 1, color: 'text-muted-foreground' };
    }
  };

  // Get badge variant based on visibility ratio
  const getBadgeVariant = () => {
    const ratio = visibleCount / totalCount;
    if (ratio >= 0.7) return 'default';
    if (ratio >= 0.4) return 'secondary';
    return 'destructive';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={disabled}
                className="relative"
              >
                <Columns3 className="h-4 w-4" />
                <Badge
                  variant={getBadgeVariant()}
                  className="absolute -top-1 -right-1 h-5 min-w-[40px] text-[10px] font-semibold px-1"
                >
                  {visibleCount}/{totalCount}
                </Badge>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>列管理 ({visibleCount}/{totalCount})</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        align="end"
        className="w-[380px] max-h-[580px] p-0"
      >
        {/* Enhanced Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-primary">列管理</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>帮助</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex items-center justify-between mb-3">
            <Badge variant={getBadgeVariant()}>
              {visibleCount} / {totalCount} 列可见
            </Badge>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCategoryToggle('basic', true)}
                className="text-xs h-7"
              >
                显示常用
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="text-xs h-7"
              >
                重置
              </Button>
            </div>
          </div>

          {/* Search Field */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜索列..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-muted"
            />
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {/* Categorized Fields */}
          {Object.entries(categorizedFields).map(([categoryKey, category]) => {
            const categoryVisibleCount = category.fields.filter(field =>
              currentColumns.includes(field)
            ).length;
            const categoryTotalCount = category.fields.length;
            const isExpanded = expandedCategories[categoryKey];

            return (
              <div key={categoryKey} className="mb-2">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryKey)}
                  className="w-full flex items-center justify-between p-2 bg-muted rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{category.icon}</span>
                    <span className="text-sm font-medium">{category.label}</span>
                    <Badge
                      variant={categoryVisibleCount > 0 ? 'default' : 'secondary'}
                      className="h-[18px] text-[10px]"
                    >
                      {categoryVisibleCount}/{categoryTotalCount}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategoryToggle(categoryKey, true);
                            }}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>显示全部 {category.label}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategoryToggle(categoryKey, false);
                            }}
                          >
                            <EyeOff className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>隐藏全部 {category.label}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </button>

                {/* Category Fields */}
                <Collapsible open={isExpanded}>
                  <CollapsibleContent>
                    <div className="pl-2 pr-2 pt-1">
                      {category.fields.map(fieldId => {
                        const isVisible = currentColumns.includes(fieldId);
                        const isRequired = requiredFields.includes(fieldId);
                        const metadata = FIELD_METADATA[fieldId] || {};
                        const usageIndicator = getUsageIndicator(metadata.usage);

                        return (
                          <button
                            key={fieldId}
                            onClick={(e) => !isRequired && handleToggleColumn(fieldId, e)}
                            disabled={isRequired}
                            className={`w-full flex items-center gap-2 p-2 rounded-lg my-0.5 ${
                              isRequired
                                ? 'opacity-70 cursor-not-allowed'
                                : 'hover:bg-muted cursor-pointer'
                            }`}
                          >
                            <Checkbox
                              checked={isVisible}
                              disabled={isRequired}
                              onCheckedChange={() => !isRequired && handleToggleColumn(fieldId)}
                            />
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                  {metadata.label || fieldId}
                                </span>
                                {isRequired && (
                                  <Badge variant="destructive" className="h-4 text-[9px]">
                                    必需
                                  </Badge>
                                )}
                              </div>
                              {metadata.description && (
                                <p className="text-xs text-muted-foreground">
                                  {metadata.description}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-0.5">
                              {[...Array(3)].map((_, index) => (
                                <Star
                                  key={index}
                                  className={`h-3 w-3 ${
                                    index < usageIndicator.stars
                                      ? usageIndicator.color
                                      : 'text-muted-foreground'
                                  }`}
                                  fill={index < usageIndicator.stars ? 'currentColor' : 'none'}
                                />
                              ))}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}

          {Object.keys(categorizedFields).length === 0 && (
            <div className="p-6 text-center">
              <p className="text-sm text-muted-foreground">
                {searchTerm ? '未找到匹配的列' : '无可用列'}
              </p>
            </div>
          )}

          <Separator className="my-2" />

          {/* Action Items */}
          <button
            onClick={handleOpenFullManager}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted"
          >
            <Settings className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">高级设置</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnSelector;
